import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { OnboardingShell } from '@/onboarding/OnboardingShell'
import { bookmarkStep } from '@/api/onboarding'
import { usePinnedChannelUUID } from '@/onboarding/usePinnedChannelUUID'
import { createCampaigns, type Campaign, type Creative } from '@/api/creatives'
import { fireCelebration } from '@/lib/confetti'
import { track, trackError } from '@/lib/analytics'

const CAMPAIGNS_CACHE_PREFIX = 'tgx-campaigns-result-'

function loadCachedCampaigns(channelUUID: string): Campaign[] | null {
  try {
    const raw = sessionStorage.getItem(`${CAMPAIGNS_CACHE_PREFIX}${channelUUID}`)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Campaign[]
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null
  } catch {
    return null
  }
}

function saveCachedCampaigns(channelUUID: string, campaigns: Campaign[]): void {
  try {
    sessionStorage.setItem(`${CAMPAIGNS_CACHE_PREFIX}${channelUUID}`, JSON.stringify(campaigns))
  } catch (e) {
    console.warn('[campaigns-ready] cache save failed', e)
  }
}

export function CampaignsReadyPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const channelUUID = usePinnedChannelUUID()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [submitting, setSubmitting] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const locState = location.state as { creatives?: Creative[] } | null
  const creatives = locState?.creatives || []

  useEffect(() => {
    if (!channelUUID) {
      console.log('[campaigns-ready] waiting for channelUUID')
      return
    }
    if (campaigns.length > 0) return

    // Уже создавали кампании в этой сессии — переиспользуем кеш, не дёргаем бэк повторно
    const cached = loadCachedCampaigns(channelUUID)
    if (cached) {
      console.log('[campaigns-ready] loaded from cache', cached.length)
      setCampaigns(cached)
      setSubmitting(false)
      return
    }

    if (creatives.length === 0) {
      console.warn('[campaigns-ready] no creatives in location.state')
      setError('Нет креативов для сборки кампаний. Вернитесь и создайте хотя бы один.')
      setSubmitting(false)
      return
    }
    console.log('[campaigns-ready] starting create-campaigns', { channelUUID, count: creatives.length })
    setSubmitting(true)
    createCampaigns(channelUUID, creatives)
      .then((res) => {
        console.log('[campaigns-ready] got campaigns', res)
        if (res.campaigns && res.campaigns.length > 0) {
          setCampaigns(res.campaigns)
          saveCachedCampaigns(channelUUID, res.campaigns)
          fireCelebration()
          track('campaigns_ready', {
            count: res.campaigns.length,
            message: `кампании созданы · ${res.campaigns.length} шт`,
          })
        }
        // Если ответ пустой/нераспознан — не делаем confetti.
        // UI покажет состояние "не подтверждено" с инструкцией проверить вручную.
      })
      .catch((e) => {
        console.error('[campaigns-ready] create-campaigns error', e)
        const code = (e as { code?: string })?.code
        if (code === 'REQUISITES_REQUIRED') {
          bookmarkStep('requisites')
          navigate('/onboarding/requisites?from=campaigns', { replace: true })
          return
        }
        trackError(e, 'create-campaigns')
        setError(e instanceof Error ? e.message : 'Не удалось собрать кампании')
      })
      .finally(() => setSubmitting(false))
  }, [channelUUID, creatives, campaigns.length, navigate])

  function handleNext() {
    bookmarkStep('final')
    navigate('/onboarding/final')
  }

  return (
    <OnboardingShell
      step="campaigns-ready"
      footer={
        <>
          <button className="foot-back" onClick={() => navigate('/onboarding/creatives-editor')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Назад
          </button>
          <div className="foot-spacer" />
          <button className="foot-next" onClick={handleNext} disabled={submitting}>
            Завершить
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </>
      }
    >
      <div className="success-wrap">
        {submitting && (
          <div className="building-card">
            <div className="spinner-large" />
            <div className="building-title">Собираем кампании в Я.Директе…</div>
            <div className="building-sub">Это занимает 10–30 секунд. Не закрывайте вкладку.</div>
          </div>
        )}

        {!submitting && !error && campaigns.length > 0 && (
          <>
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12l4 4L19 6" />
              </svg>
            </div>
            <p className="stage-eyebrow center">
              <span className="stage-eyebrow-pill success">Кампании собраны</span>
            </p>
            <h1 className="stage-title center">
              Кампании <em>в&nbsp;кабинете Я.Директа</em>.
            </h1>
            <p className="stage-sub center">
              {campaigns.length} {pluralCampaigns(campaigns.length)}, ставки и цели&nbsp;— уже там. Тариф TeleGraphyx уже оплачен — осталось <b>пополнить бюджет Я.Директа</b> и запустить.
            </p>

            <div className="campaigns-summary">
              {campaigns.map((c) => (
                <CampaignRow key={c.id} campaign={c} />
              ))}
            </div>

            <div className="success-eta warn">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 9v4M12 17v.01M10.3 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.7 3.86a2 2 0 0 0-3.4 0z" />
              </svg>
              Не забудьте <b>пополнить кабинет Я.Директа</b> после старта тарифа&nbsp;— мы не храним ваши деньги, рекламный бюджет идёт напрямую в Яндекс.
            </div>
          </>
        )}

        {!submitting && !error && campaigns.length === 0 && (
          <div className="uncertain-card">
            <div className="uncertain-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4M12 16v.01" />
              </svg>
            </div>
            <h1 className="uncertain-title">Запрос отправлен, нужна ручная проверка</h1>
            <p className="uncertain-text">
              Мы отправили данные в Я.Директ, но не получили обратно подтверждения с деталями кампаний.{' '}
              <b>Откройте кабинет Я.Директа и проверьте, что появились свежие черновики.</b>
            </p>
            <div className="uncertain-actions">
              <a className="uncertain-link" href="https://direct.yandex.ru/" target="_blank" rel="noopener">
                Открыть кабинет Я.Директа →
              </a>
            </div>
            <div className="uncertain-hint">
              Если кампаний там нет&nbsp;— пройдите этот шаг ещё раз. Если есть&nbsp;— продолжайте к тарифу.
            </div>
          </div>
        )}

        {error && (
          <div className="error-card">
            <div className="error-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 8v4M12 16v.01" />
              </svg>
            </div>
            <h1 className="error-title">Не удалось собрать кампании</h1>
            <p className="error-text">
              Произошла техническая ошибка при создании кампаний в&nbsp;Я.Директе. Это может быть связано с&nbsp;настройками рекламного кабинета.
            </p>
            <details className="error-details">
              <summary>Технические детали</summary>
              <code>{truncateErrorBody(error)}</code>
            </details>
            <div className="error-actions">
              <button className="error-retry" onClick={() => window.location.reload()}>
                Попробовать снова
              </button>
              <button className="error-skip" onClick={() => navigate('/onboarding/final')}>
                Пропустить — завершить
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .success-wrap {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          max-width: 760px;
          margin: 32px auto 0;
        }
        .success-icon {
          width: 96px;
          height: 96px;
          border-radius: 50%;
          background: var(--tg-up, #00a860);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 28px;
          box-shadow: 0 12px 32px -8px rgba(0, 168, 96, 0.5);
          animation: success-pop 0.5s cubic-bezier(0.2,0.8,0.4,1) both;
        }
        .success-icon svg { width: 48px; height: 48px; }
        @keyframes success-pop {
          0% { transform: scale(0.6); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }

        .building-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 16px;
          padding: 36px 40px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          margin-top: 32px;
        }
        .spinner-large {
          width: 48px;
          height: 48px;
          border: 4px solid var(--tg-line-2, #f0f0f4);
          border-top-color: var(--tg-blue, #0080ff);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .building-title {
          font-weight: 700;
          font-size: 16px;
          color: var(--tg-ink, #111);
        }
        .building-sub {
          font-size: 13px;
          color: var(--tg-mute, #8f9eb0);
        }

        .campaigns-summary {
          margin-top: 24px;
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .success-eta {
          margin-top: 16px;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 12px 18px;
          background: rgba(255, 184, 0, 0.12);
          border: 1px solid rgba(255, 184, 0, 0.3);
          border-radius: 10px;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          text-align: left;
          line-height: 1.5;
        }
        .success-eta b { color: var(--tg-ink, #111); font-weight: 700; }
        .success-eta svg {
          width: 18px;
          height: 18px;
          color: rgb(180, 130, 0);
          flex-shrink: 0;
        }
        .onb-error-inline {
          margin-top: 16px;
          padding: 12px 16px;
          background: rgba(255, 64, 0, 0.08);
          border-radius: 10px;
          color: #c33;
          font-size: 13px;
        }

        .uncertain-card, .error-card {
          margin: 32px auto 0;
          max-width: 540px;
          padding: 28px 32px;
          background: var(--tg-surface, #fff);
          border-radius: 16px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }
        .uncertain-card {
          border: 1px solid rgba(255, 184, 0, 0.3);
          background: linear-gradient(180deg, rgba(255, 184, 0, 0.06), white);
        }
        .error-card {
          border: 1px solid rgba(255, 64, 0, 0.3);
          background: linear-gradient(180deg, rgba(255, 64, 0, 0.06), white);
        }
        .uncertain-icon, .error-icon {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 4px;
        }
        .uncertain-icon { background: rgba(255, 184, 0, 0.16); color: rgb(180, 130, 0); }
        .error-icon { background: rgba(255, 64, 0, 0.12); color: #c33; }
        .uncertain-icon svg, .error-icon svg { width: 28px; height: 28px; }
        .uncertain-title, .error-title {
          margin: 0;
          font-size: 20px;
          font-weight: 800;
          color: var(--tg-ink, #111);
        }
        .uncertain-text, .error-text {
          margin: 0;
          font-size: 14px;
          line-height: 1.55;
          color: var(--tg-ink-2, #333);
        }
        .uncertain-text b, .error-text b { color: var(--tg-ink, #111); font-weight: 700; }
        .uncertain-actions {
          margin-top: 8px;
        }
        .uncertain-link {
          display: inline-flex;
          align-items: center;
          padding: 10px 18px;
          background: var(--tg-blue, #0080ff);
          color: white;
          border-radius: 10px;
          font-weight: 700;
          font-size: 14px;
          text-decoration: none;
        }
        .uncertain-link:hover { background: var(--tg-blue-hover, #0066cc); }
        .uncertain-hint {
          margin-top: 4px;
          font-size: 12px;
          color: var(--tg-mute, #8f9eb0);
        }
        .error-actions {
          display: flex;
          gap: 8px;
          margin-top: 8px;
          flex-wrap: wrap;
          justify-content: center;
        }
        .error-retry, .error-skip {
          padding: 10px 18px;
          border: 0;
          border-radius: 10px;
          font-family: inherit;
          font-weight: 700;
          font-size: 14px;
          cursor: pointer;
        }
        .error-retry {
          background: #c33;
          color: white;
        }
        .error-retry:hover { background: #a22; }
        .error-skip {
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-ink, #111);
        }
        .error-skip:hover { background: var(--tg-line-2, #f0f0f4); }
        .error-details {
          margin-top: 8px;
          width: 100%;
          font-size: 12px;
          text-align: left;
        }
        .error-details summary {
          cursor: pointer;
          color: var(--tg-mute, #8f9eb0);
          font-weight: 600;
          padding: 6px 0;
        }
        .error-details summary:hover {
          color: var(--tg-ink-2, #333);
        }
        .error-details code {
          display: block;
          margin-top: 8px;
          padding: 10px 12px;
          background: rgba(0,0,0,0.04);
          border-radius: 8px;
          font-family: ui-monospace, SFMono-Regular, monospace;
          font-size: 11px;
          line-height: 1.5;
          color: var(--tg-ink-2, #333);
          word-break: break-word;
          white-space: pre-wrap;
          max-height: 200px;
          overflow-y: auto;
        }
      `}</style>
    </OnboardingShell>
  )
}

function CampaignRow({ campaign }: { campaign: Campaign }) {
  const parts: string[] = []
  if (campaign.cpc && campaign.cpc > 0) parts.push(`CPC ~${campaign.cpc} ₽`)
  if (campaign.adsCount && campaign.adsCount > 0) parts.push(`${campaign.adsCount} ${pluralAds(campaign.adsCount)}`)
  if (campaign.network) parts.push(campaign.network)
  if (campaign.segment && parts.length === 0) parts.push(campaign.segment)
  const subline = parts.join(' · ')
  return (
    <div className="camp-row">
      <div className="camp-row-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 6v6l4 2" />
        </svg>
      </div>
      <div className="camp-row-meta">
        <div className="camp-row-name">{campaign.name}</div>
        {subline && <div className="camp-row-sub">{subline}</div>}
      </div>
      <div className="camp-row-status">{campaign.status}</div>
      <style>{`
        .camp-row {
          display: grid;
          grid-template-columns: 36px 1fr auto;
          gap: 14px;
          align-items: center;
          padding: 14px 18px;
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 12px;
          text-align: left;
        }
        .camp-row-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: var(--tg-blue-bg, #ebf2ff);
          color: var(--tg-blue-press, #0066cc);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .camp-row-icon svg { width: 18px; height: 18px; }
        .camp-row-name {
          font-weight: 700;
          font-size: 14px;
          color: var(--tg-ink, #111);
          margin-bottom: 3px;
        }
        .camp-row-sub {
          font-size: 12px;
          color: var(--tg-mute, #8f9eb0);
        }
        .camp-row-status {
          padding: 4px 10px;
          background: var(--tg-surface-2, #f5f5f9);
          color: var(--tg-mute, #8f9eb0);
          font-weight: 700;
          font-size: 10px;
          border-radius: 999px;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .camp-row-meta { min-width: 0; }
        .camp-row-name { word-break: break-all; }
        @media (max-width: 640px) {
          .camp-row {
            padding: 12px 14px;
            gap: 10px;
          }
          .camp-row-name { font-size: 13px; }
          .camp-row-status { font-size: 9px; padding: 3px 8px; }
        }
      `}</style>
    </div>
  )
}

function pluralCampaigns(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'кампания'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'кампании'
  return 'кампаний'
}

function truncateErrorBody(error: string): string {
  // Pilotad может вернуть HTML 400 страницы как часть error-string — показываем кратко
  const htmlIdx = error.indexOf('<html')
  if (htmlIdx > 0) {
    return error.slice(0, htmlIdx).trim() + ' (HTML-ответ опущен)'
  }
  if (error.length > 400) {
    return error.slice(0, 400) + '…'
  }
  return error
}

function pluralAds(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'объявление'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'объявления'
  return 'объявлений'
}
